<main class="about-v2">
  <!-- HERO -->
  <section class="hero">
    <div class="wrap">
      <div class="hero-copy">
        <p class="eyebrow">The private registration market</p>
        <h1>Bringing the private registration market together.</h1>
        <p class="lede">
          Private registrations are spread across hundreds of dealers, DVLA stock
          and auction results. PlateX brings dealer stock, DVLA registrations,
          market data and sales history together in one place — to search,
          compare and understand.
        </p>
        <div class="hero-cta">
          <a class="btn btn-primary" [routerLink]="searchRoute">Search Registrations</a>
          <a class="btn btn-ghost" [routerLink]="dealerRoute">Dealer Enquiries</a>
        </div>
      </div>

      <div class="hero-art">
        <div class="index-panel" role="img" aria-label="PlateX market coverage scope">
          <div class="ip-head">
            <span>PlateX · Market coverage</span>
            <span class="live"><i></i>Indexing</span>
          </div>
          <div class="ip-hero">
            <div class="big">{{ heroValue }}<span class="suffix">{{ heroSuffix }}</span></div>
            <div class="cap">Searchable registrations across the market</div>
          </div>
          <div class="ip-row">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="m20 6-11 11-5-5"/></svg>
            Dealer stock, gathered market-wide
          </div>
          <div class="ip-row">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="m20 6-11 11-5-5"/></svg>
            Unissued DVLA registrations
          </div>
          <div class="ip-row">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="m20 6-11 11-5-5"/></svg>
            Every registration class
          </div>
          <div class="ip-row">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="m20 6-11 11-5-5"/></svg>
            Historic sales &amp; pricing data
          </div>
          <div class="ip-foot">One search across the whole market</div>
        </div>
      </div>
    </div>
  </section>

  <!-- METRICS -->
  <section class="metrics" aria-label="Platform scale">
    <div class="wrap">
      <div class="head">
        <p class="eyebrow on-dark">The market, in numbers</p>
        <h2>The scale behind PlateX.</h2>
        <p>Live figures from the PlateX platform.</p>
      </div>
      <div class="metric-grid">
        <pla-metric-card
          *ngFor="let key of metricKeys"
          [metric]="metrics[key]"
          [onDark]="true"
        ></pla-metric-card>
      </div>
      <p class="metric-note">
        Served from the PlateX metrics service · updates automatically · fallback
        values shown if briefly unavailable.
      </p>
    </div>
  </section>

  <!-- WHAT PLATEX PROVIDES -->
  <section class="provides">
    <div class="wrap">
      <div class="head">
        <p class="eyebrow">What PlateX provides</p>
        <h2>Three things, done across the whole market.</h2>
      </div>
      <div class="pillars">
        <div class="pillar">
          <div class="ico" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/></svg>
          </div>
          <h3>Market discovery</h3>
          <p>Search registrations from across the market in one place.</p>
        </div>
        <div class="pillar">
          <div class="ico" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19V5"/><path d="M4 19h16"/><path d="m7 14 3.5-4 3 2.5L20 6"/></svg>
          </div>
          <h3>Market intelligence</h3>
          <p>Historical sales data, pricing analysis and market insight.</p>
        </div>
        <div class="pillar">
          <div class="ico" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3 3 8l9 5 9-5-9-5Z"/><path d="m3 16 9 5 9-5"/><path d="m3 12 9 5 9-5"/></svg>
          </div>
          <h3>Market infrastructure</h3>
          <p>Tools and services that help buyers and dealers operate more efficiently.</p>
        </div>
      </div>
    </div>
  </section>

  <!-- HOW MARKETS MATURE -->
  <section class="concept">
    <div class="wrap">
      <div class="head">
        <p class="eyebrow">How markets mature</p>
        <h2>Established markets are built on shared tools and data.</h2>
        <p>
          Every mature market depends on things its participants take for granted —
          central places to discover what's available, reference points for value,
          and published data that shows how the market is moving.
        </p>
      </div>
      <div class="principle">
        <div class="col">
          <div class="k">Property</div>
          <p>Central listings, registered ownership, published price indices and regular market reports.</p>
        </div>
        <div class="col">
          <div class="k">Vehicles</div>
          <p>Aggregated listings and established pricing guides built on years of sales history.</p>
        </div>
        <div class="col">
          <div class="k">Financial markets</div>
          <p>Exchanges, indices and dedicated market-data providers everyone can reference.</p>
        </div>
      </div>
      <p class="close">
        Private registrations have grown into an asset market of real scale — yet
        much of this has never existed for them.
        <span class="hl">PlateX is helping to build it.</span>
      </p>
    </div>
  </section>

  <!-- WHY -->
  <section class="why">
    <div class="wrap">
      <div class="av-lead">
        <p class="eyebrow">Why PlateX exists</p>
        <p>A market this fragmented carries a cost — and everyone in it pays.</p>
        <p class="soft">Spread across dealer websites, DVLA stock and auction platforms, buyers rarely see the whole market and dealers compete for attention in dozens of different places.</p>
      </div>
      <div class="cost-h">The cost of a fragmented market</div>
      <div class="costs">
        <div class="cost"><span class="n">01</span><span>Limited transparency across the market</span></div>
        <div class="cost"><span class="n">02</span><span>Inconsistent understanding of value</span></div>
        <div class="cost"><span class="n">03</span><span>Stock that's hard to discover</span></div>
        <div class="cost"><span class="n">04</span><span>Higher acquisition costs for dealers</span></div>
        <div class="cost"><span class="n">05</span><span>Duplicated effort across the industry</span></div>
        <div class="cost"><span class="n">06</span><span>No market-wide visibility</span></div>
        <div class="cost"><span class="n">07</span><span>Long-term trends are hard to read</span></div>
      </div>
      <p class="resolve">PlateX is built to close these gaps — for buyers, for dealers, and for the market as a whole.</p>
    </div>
  </section>

  <!-- BUYERS & DEALERS -->
  <section class="sides">
    <div class="wrap">
      <div class="av-lead">
        <p class="eyebrow">Both sides of the market</p>
        <h2>Bringing the market together creates efficiencies for everyone in it.</h2>
        <p>
          A single dealer may hold thousands of registrations — but a buyer
          searching one inventory often leaves without the right plate. When stock
          from across the market sits in one place, buyers discover far more of it
          and dealers reach more relevant demand.
        </p>
        <p>
          As more of the market becomes visible in one place, buyers spend less
          time searching and dealers spend less time competing for attention — and
          more searches end in a successful match.
        </p>
      </div>
      <div class="av-col2">
        <div class="side buyers">
          <p class="tag">For buyers</p>
          <h3>See the whole market with confidence</h3>
          <ul>
            <li><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m20 6-11 11-5-5"/></svg> Broader choice across the market</li>
            <li><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m20 6-11 11-5-5"/></svg> Easier discovery of available registrations</li>
            <li><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m20 6-11 11-5-5"/></svg> Pricing evidence, not guesswork</li>
            <li><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m20 6-11 11-5-5"/></svg> Greater confidence in the market</li>
            <li><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m20 6-11 11-5-5"/></svg> Easier access to the whole market</li>
          </ul>
        </div>
        <div class="side dealers">
          <p class="tag">For dealers</p>
          <h3>Reach more relevant demand</h3>
          <ul>
            <li><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m20 6-11 11-5-5"/></svg> Aggregated demand from across the market</li>
            <li><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m20 6-11 11-5-5"/></svg> Improved discoverability of stock</li>
            <li><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m20 6-11 11-5-5"/></svg> Lower customer acquisition costs</li>
            <li><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m20 6-11 11-5-5"/></svg> Less dependence on SEO and paid advertising</li>
            <li><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m20 6-11 11-5-5"/></svg> Better matching between buyers and stock</li>
          </ul>
        </div>
      </div>
    </div>
  </section>

  <!-- TIMELINE -->
  <section class="timeline-sec">
    <div class="wrap">
      <div class="av-lead">
        <p class="eyebrow">Platform development</p>
        <h2>Built step by step.</h2>
        <p>Assembled over years, not launched overnight.</p>
      </div>
      <div class="timeline">
        <div class="tl"><span class="yr">2023</span><span class="dot"></span><h3>Foundations &amp; indexing</h3><p>The first market index and data foundations are built.</p></div>
        <div class="tl"><span class="yr">2024</span><span class="dot"></span><h3>Public launch</h3><p>Public launch and large-scale registration indexing.</p></div>
        <div class="tl"><span class="yr">2025</span><span class="dot"></span><h3>Aggregation &amp; history</h3><p>Dealer aggregation, price comparison and historical sales data expand.</p></div>
        <div class="tl"><span class="yr">2026</span><span class="dot"></span><h3>Intelligence &amp; sold data</h3><p>Market intelligence, sold-data records and PlateX Index development.</p></div>
        <div class="tl future"><span class="yr">Future</span><span class="dot"></span><h3>A market reference point</h3><p>A credible market index and continued development.</p></div>
      </div>
    </div>
  </section>

  <!-- THE PLATE REGISTRY + specimen -->
  <section class="record">
    <div class="wrap">
      <div class="copy">
        <p class="eyebrow">The Plate Registry</p>
        <h2>Preserving the market's history.</h2>
        <p>Every sale, listing and market observation recorded by PlateX contributes to the Plate Registry — a growing record of the private registration market.</p>
        <p>Over time, individual registrations can develop a discoverable history of pricing, availability, sales activity and market context. The more of that record that is preserved, the more useful it becomes to buyers, collectors, dealers and researchers alike.</p>
      </div>
      <div class="specimen" role="img" aria-label="Illustration of a registration record for A 22">
        <div class="sp-head"><span>Registration record</span><span>Dateless</span></div>
        <div class="sp-plate">
          <pla-plate
            [rawPlate]="'A 22'"
            [openModal]="false"
            [clickThrough]="false"
            [center]="true"
            [noMargin]="true"
          ></pla-plate>
        </div>
        <div class="sp-rows">
          <div class="sp-row"><span class="k">First indexed</span><span class="v">2024</span></div>
          <div class="sp-row"><span class="k">Times listed</span><span class="v">4 dealers</span></div>
          <div class="sp-row"><span class="k">Observed range</span><span class="v">£18k – £24k</span></div>
          <div class="sp-row"><span class="k">Last seen</span><span class="v">2026</span></div>
        </div>
        <div class="sp-foot">Illustrative record</div>
      </div>
    </div>
  </section>

  <!-- AMBITION -->
  <section class="ambition">
    <div class="wrap">
      <p class="eyebrow">Where it's going</p>
      <h2>A better-supported market is a stronger market.</h2>
      <p class="intro">
        Markets become larger, more transparent, more accessible and better
        understood when they're supported by reliable data and shared tools.
        <span class="hl">That is what PlateX is working towards</span> — for the
        whole private registration market.
      </p>
      <div class="outcomes">
        <div class="outcome"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="m20 6-11 11-5-5"/></svg> More informed buyers</div>
        <div class="outcome"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="m20 6-11 11-5-5"/></svg> Better-supported dealers</div>
        <div class="outcome"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="m20 6-11 11-5-5"/></svg> Stronger valuation methods</div>
        <div class="outcome"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="m20 6-11 11-5-5"/></svg> Long-term market analysis</div>
        <div class="outcome"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="m20 6-11 11-5-5"/></svg> Credible market indices</div>
        <div class="outcome"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="m20 6-11 11-5-5"/></svg> Greater confidence in the asset class</div>
      </div>
      <p class="close">The aim is steady, durable progress for the market over the long term.</p>
    </div>
  </section>

  <!-- FOUNDER NOTE -->
  <section class="founder">
    <div class="wrap">
      <p class="eyebrow">A note from the founder</p>
      <blockquote>
        <p>
          My background is in property — a market where shared tools do a lot of
          quiet work: central places to search, published prices, records people
          can rely on. Private registrations had grown into a serious market
          without much of that. Buyers couldn't see all of it; dealers worked in
          isolation. PlateX began as a way to help close that gap, and make the
          market easier to search, compare and understand.
        </p>
      </blockquote>
      <p class="sig">— Founder, PlateX</p>
      <div class="founder-contact">
        <div class="fc-text">
          <strong>Want to see what we're building, or just have a chat?</strong>
          <span>I read everything that comes in — suggestions, ideas and constructive criticism are all welcome.</span>
        </div>
        <a class="btn btn-primary" [href]="founderHref">Message the Founder</a>
      </div>
    </div>
  </section>

  <!-- FINAL CTA -->
  <section class="final">
    <div class="wrap">
      <h2>Explore the private registration market with more clarity.</h2>
      <p>Search registrations from across the market and discover what's available.</p>
      <div class="av-row">
        <a class="btn btn-primary" [routerLink]="searchRoute">Search Registrations</a>
        <a class="btn btn-ghost on-dark" [href]="contactHref">Contact PlateX</a>
      </div>
    </div>
  </section>
</main>
